/**
 * Markdown for a CMS page — FRONTEND.md §9.169.
 *
 * **The owner's request:** *"Add .MD support and all things for .MD in CMS stránky - Obsah stránky,
 * so that we can format it etc... like.. make it actually like an editor."*
 *
 * **Escape first, then format.** Every `<`, `>` and `&` in the source becomes an entity **before** a
 * single markdown rule runs, so the only tags in the output are the ones written here. That ordering
 * is the whole security argument: the result is handed to `dangerouslySetInnerHTML`, and a renderer
 * that let raw HTML through would be stored XSS on a page anybody with the manager password can
 * edit. The existing HTML pages keep their sandboxed iframe and never reach this — `looksLikeHtml`
 * decides, exactly as it did before.
 *
 * **Written rather than installed.** A markdown library is a tooling decision `docs/DECISIONS.md`
 * would have to record, and every one of them ships raw-HTML passthrough that then has to be turned
 * off. This is the subset a restaurant's *Alergény* or *Obchodné podmienky* page needs, and it is
 * about a hundred lines with tests on each rule.
 *
 * **A single newline is a line break.** Strict markdown joins those into one paragraph; every CMS
 * page that exists today is plain text rendered with `whitespace-pre-wrap`, so joining them would
 * silently reflow live pages. GitHub made the same choice for the same reason.
 */

/** Every character that could start a tag or an entity, neutralised before any rule runs. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Every character a URL parser discards before it reads the scheme — §9.225, FB-372.
 *
 * The C0 range and DEL, which is **not** `\s`: that matches space, tab, newline, carriage return and
 * form feed, and stops there. `U+0001` is stripped by the browser and matched by neither, which is
 * how `javascript:` used to reach the attribute and run.
 */
// eslint-disable-next-line no-control-regex -- the point of the rule is the control characters
const URL_IGNORED = /[\u0000-\u0020\u007f]/g

/**
 * A link target that cannot execute.
 *
 * `javascript:` and `data:` are the two that turn a link into a script. Anything else is left alone,
 * including a relative path, because a CMS page linking to `/menu` is ordinary.
 *
 * **The scheme is tested against what the browser will see, not what was typed** — §9.225. Measured
 * in a real browser: an href of `U+0001javascript:…` resolves to `javascript:…` and executes, so a
 * check anchored on `^\s*` was reading a different string from the one that would run.
 */
function safeHref(href: string): string {
  const asBrowserSees = href.replace(URL_IGNORED, '')
  return /^(javascript|data|vbscript):/i.test(asBrowserSees) ? '#' : href.trim()
}

/**
 * Bold, italic, strikethrough and links — applied to already-escaped text.
 *
 * **Code spans are split out rather than substituted.** `` `a **b** c` `` replaced first still leaves
 * `**b**` in the string for the emphasis rule to find, and the span comes back with bold inside it —
 * caught by its own test. The first fix used a `\0` placeholder and tripped `no-control-regex`,
 * which was the linter being right for the wrong reason: splitting needs no sentinel, and a sentinel
 * is a string a determined page author could type.
 */
function emphasise(text: string): string {
  return text
    // §9.186 — *anything that does not begin the closing delimiter*, not *anything but an asterisk*.
    // The old `[^*]+` refused to match when the bold text contained italics, so `**a *b* c**` fell
    // through and printed its own asterisks. `***x***` and `*a **b** c*` happened to work, which is
    // why it went unnoticed for so long.
    //
    // Bold runs before italic deliberately: the inner `*b*` survives into the `<strong>` and the
    // italic pass below converts it there.
    // Bold *and* italic together, before either alone. Left to the two rules below, `***x***` gave
    // `<strong>*x</strong>*` and then `<strong><em>x</strong></em>` — tags crossing each other,
    // which is not HTML. Measured on the way to fixing the case above.
    .replace(/\*\*\*((?:(?!\*\*\*).)+)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*((?:(?!\*\*).)+)\*\*/g, '<strong>$1</strong>')
    .replace(/__((?:(?!__).)+)__/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/(^|[^_])_([^_\n]+)_/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del>$1</del>')
    .replace(
      /\[([^\]]+)\]\(([^)\s]+)\)/g,
      (_match, label: string, href: string) =>
        `<a href="${escapeHtml(safeHref(href))}" target="_blank" rel="noopener noreferrer">${label}</a>`,
    )
}

function inline(text: string): string {
  // Odd indices are the insides of code spans; even ones are ordinary text.
  return text
    .split(/`([^`]+)`/g)
    .map((part, index) => (index % 2 === 1 ? `<code>${part}</code>` : emphasise(part)))
    .join('')
}

/**
 * Markdown to HTML. The input is untrusted; the output is not.
 *
 * Block rules, in the order a line is tested: fenced code, heading, rule, quote, list, paragraph.
 */
export function renderMarkdown(source: string): string {
  const lines = escapeHtml(source).split('\n')
  const out: string[] = []
  let paragraph: string[] = []
  let list: { ordered: boolean; items: string[] } | null = null
  let quote: string[] | null = null
  let fence: string[] | null = null

  const flushParagraph = () => {
    if (paragraph.length === 0) return
    // A single newline inside a paragraph is a break, not a join — see the module note.
    out.push(`<p>${inline(paragraph.join('<br />'))}</p>`)
    paragraph = []
  }
  const flushList = () => {
    if (list === null) return
    const tag = list.ordered ? 'ol' : 'ul'
    out.push(`<${tag}>${list.items.map((item) => `<li>${inline(item)}</li>`).join('')}</${tag}>`)
    list = null
  }
  const flushQuote = () => {
    if (quote === null) return
    out.push(`<blockquote><p>${inline(quote.join('<br />'))}</p></blockquote>`)
    quote = null
  }
  const flushAll = () => {
    flushParagraph()
    flushList()
    flushQuote()
  }

  for (const line of lines) {
    // A fence swallows everything until it closes: nothing inside it is markdown.
    if (/^\s*```/.test(line)) {
      if (fence === null) {
        flushAll()
        fence = []
      } else {
        out.push(`<pre><code>${fence.join('\n')}</code></pre>`)
        fence = null
      }
      continue
    }
    if (fence !== null) {
      fence.push(line)
      continue
    }

    if (line.trim() === '') {
      flushAll()
      continue
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line)
    if (heading !== null) {
      flushAll()
      const level = heading[1]!.length
      out.push(`<h${level}>${inline(heading[2]!.trim())}</h${level}>`)
      continue
    }

    if (/^\s*(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      flushAll()
      out.push('<hr />')
      continue
    }

    const quoted = /^\s*&gt;\s?(.*)$/.exec(line)
    if (quoted !== null) {
      flushParagraph()
      flushList()
      quote = quote ?? []
      quote.push(quoted[1]!)
      continue
    }

    const bullet = /^\s*[-*+]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (bullet !== null || numbered !== null) {
      flushParagraph()
      flushQuote()
      const ordered = numbered !== null
      // A list that changes kind mid-way is two lists, which is what the source says.
      if (list !== null && list.ordered !== ordered) flushList()
      list = list ?? { ordered, items: [] }
      list.items.push((bullet?.[1] ?? numbered?.[1] ?? '').trim())
      continue
    }

    flushList()
    flushQuote()
    paragraph.push(line.trim())
  }

  if (fence !== null) {
    // An unclosed fence is still code — better than reading the rest of the page as markdown.
    out.push(`<pre><code>${fence.join('\n')}</code></pre>`)
  }
  flushAll()

  return out.join('\n')
}
